import styled from 'styled-components';
import { GoodConnectionIcon } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/components/JoinEvent/icons/GoodConnectionIcon';
import { GreatConnectionIcon } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/components/JoinEvent/icons/GreatConnectionIcon';
import { PoorConnectionIcon } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/components/JoinEvent/icons/PoorConnectionIcon';
import { DisconnectedConnectionIcon } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/components/JoinEvent/icons/DisconnectedConnectionIcon';
import { selectConnectionQualityByPeerID, useHMSStore } from '@100mslive/react-sdk';
import { Icon, Text, Tooltip } from '@wistia/vhs';
import { FancyTooltip } from '~/onboarding';
import { match } from 'ts-pattern';
import type { Nullable } from '~/utilities/type-guards';
import { isNotNil } from '~/utilities/type-guards';
import type { JSX } from 'react';
import { useEffect, useState, useRef } from 'react';
import { useLiveLayoutContext } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/store/LiveLayoutProvider';

const POSITIVE_CONNECTION_QUALITY = 'positiveConnectionQuality';
const POOR_CONNECTION_QUALITY = 'poorConnectionQuality';
const NO_CONNECTION_QUALITY = 'noConnectionQuality';
const INVALID_CONNECTION_QUALITY = 'invalidConnectionQuality';

const NO_CONNECTION = 'No connection';
const NO_CONNECTION_TOOLTIP = 'Check your internet connection or reconnect.';
const POOR_CONNECTION = 'Poor connection';
const POOR_CONNECTION_TOOLTIP = 'Move to an area with stronger wifi.';
const GOOD_CONNECTION = 'Good connection';
const GOOD_CONNECTION_TOOLTIP = 'Close unnecessary tabs or apps.';
const EXCELLENT_CONNECTION = 'Excellent connection';
const UNSTABLE_TOOLTIP_MESSAGE_1 = 'Your connection seems to be unstable.';
const UNSTABLE_TOOLTIP_MESSAGE_2 = 'Click here for tips to troubleshoot the issue.';

const TOOLTIP_MESSAGE = 'Network strength';

export type NetworkMessage = {
  header: string;
  body: string;
};

export const NETWORK_MESSAGES: Record<'DISCONNECTED' | 'SUBOPTIMAL' | 'UNSTABLE', NetworkMessage> =
  {
    UNSTABLE: {
      header: 'Unstable network detected',
      body: 'Try switching to a more stable network or restart your router to improve connection.',
    },
    SUBOPTIMAL: {
      header: 'Connection can be improved',
      body: 'Close unnecessary tabs or apps to improve network quality.',
    },
    DISCONNECTED: {
      header: 'Network disconnected',
      body: 'Check your internet connection or reconnect.',
    },
  };

const TIME_OUT = 3000;
const TOOLTIP_DELAY = 1500;

type DownlinkQuality = number | undefined;

type ConnectionQualityData =
  | {
      type: 'noConnectionQuality' | 'poorConnectionQuality' | 'positiveConnectionQuality';
      icon: () => JSX.Element;
      text: string;
      tooltip: Nullable<{ text: string }>;
    }
  | { type: 'invalidConnectionQuality' };

const ConnectionWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0.25rem 0.25rem;
`;

interface IconWrapperProps {
  backgroundColor?: string;
}

const IconWrapper = styled.div<IconWrapperProps>`
  background-color: ${({ backgroundColor = 'rgba(0, 0, 0, 70%)' }) => backgroundColor};
  border-radius: 2px;
  height: 1.25rem;
  padding: 2px;
  width: 1.25rem;
`;

const NetworkConnectionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
`;

const StyledTooltip = styled(Tooltip)`
  text-align: left;
  background: ${({ theme }) => theme.color.grey1100};
`;

const getConnectionQuality = (downlinkQuality?: DownlinkQuality) =>
  match<DownlinkQuality, ConnectionQualityData>(downlinkQuality)
    .with(-1, () => ({
      type: INVALID_CONNECTION_QUALITY,
    }))
    .with(1, 2, () => ({
      type: POOR_CONNECTION_QUALITY,
      icon: PoorConnectionIcon,
      text: POOR_CONNECTION,
      tooltip: {
        text: POOR_CONNECTION_TOOLTIP,
      },
    }))
    .with(3, 4, () => ({
      type: POSITIVE_CONNECTION_QUALITY,
      icon: GoodConnectionIcon,
      text: GOOD_CONNECTION,
      tooltip: { text: GOOD_CONNECTION_TOOLTIP },
    }))
    .with(5, () => ({
      type: POSITIVE_CONNECTION_QUALITY,
      icon: GreatConnectionIcon,
      text: EXCELLENT_CONNECTION,
      tooltip: null,
    }))
    .otherwise(() => ({
      type: NO_CONNECTION_QUALITY,
      icon: DisconnectedConnectionIcon,
      text: NO_CONNECTION,
      tooltip: { text: NO_CONNECTION_TOOLTIP },
    }));

const useConnectionIndicatorDelay = (delay = TIME_OUT) => {
  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowIndicator(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);
  return showIndicator;
};

export const PreviewConnectionIndicator = ({
  particpantId,
}: {
  particpantId?: string;
}): JSX.Element | null => {
  const downlinkQuality = useHMSStore(
    selectConnectionQualityByPeerID(particpantId),
  )?.downlinkQuality;
  const connectionQuality = getConnectionQuality(downlinkQuality);

  const showIndicator = useConnectionIndicatorDelay();

  if (!showIndicator || connectionQuality.type === INVALID_CONNECTION_QUALITY) {
    return null;
  }

  const { icon: ConnectionIcon, text, tooltip } = connectionQuality;

  return (
    <ConnectionWrapper>
      <ConnectionIcon />
      <Text
        variant="body2"
        colorOverride="grey700"
      >
        {text}
      </Text>
      {isNotNil(tooltip) ? (
        <FancyTooltip
          content={<Text variant="body1">{tooltip.text}</Text>}
          direction="top"
          fixedWidth={true}
        >
          <Icon
            size="sm"
            color="grey900"
            type="question-mark"
          />
        </FancyTooltip>
      ) : null}
    </ConnectionWrapper>
  );
};

export const TopBarConnectionIndicator = ({
  participantId,
}: {
  participantId?: string;
}): JSX.Element | null => {
  const [hasAcknowledgedTooltip, setHasAcknowledgedTooltip] = useState(false);
  const [delayedTooltipState, setDelayedTooltipState] = useState(false);
  const previousTooltipState = useRef(false);
  const hideTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { setNetworkConnectivityDrawerState } = useLiveLayoutContext();
  const downlinkQuality = useHMSStore(
    selectConnectionQualityByPeerID(participantId),
  )?.downlinkQuality;
  const connectionQuality = getConnectionQuality(downlinkQuality);
  const showIndicator = useConnectionIndicatorDelay();

  const displayTooltipAlert =
    connectionQuality.type === 'noConnectionQuality' ||
    connectionQuality.type === 'poorConnectionQuality';

  useEffect(() => {
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current);
    }

    if (displayTooltipAlert) {
      setDelayedTooltipState(true);
    }

    if (!displayTooltipAlert && previousTooltipState.current) {
      hideTooltipTimeoutRef.current = setTimeout(() => {
        setDelayedTooltipState(false);
      }, TOOLTIP_DELAY);
    }

    previousTooltipState.current = displayTooltipAlert;

    return () => {
      if (hideTooltipTimeoutRef.current) {
        clearTimeout(hideTooltipTimeoutRef.current);
      }
    };
  }, [displayTooltipAlert]);

  if (!showIndicator || connectionQuality.type === INVALID_CONNECTION_QUALITY) {
    return null;
  }

  const UNSTABLE_TOOLTIP_MESSAGE = (
    <>
      {UNSTABLE_TOOLTIP_MESSAGE_1}
      <br />
      {UNSTABLE_TOOLTIP_MESSAGE_2}
    </>
  );

  const { icon: ConnectionIcon } = connectionQuality;

  return delayedTooltipState ? (
    <NetworkConnectionButton
      aria-label="Network connection indicator"
      onClick={() => {
        setNetworkConnectivityDrawerState('open');
        setHasAcknowledgedTooltip(true);
      }}
    >
      <StyledTooltip
        label={UNSTABLE_TOOLTIP_MESSAGE}
        direction="bottom"
        forceState={!hasAcknowledgedTooltip ? 'open' : undefined}
      >
        <IconWrapper>
          <ConnectionIcon />
        </IconWrapper>
      </StyledTooltip>
    </NetworkConnectionButton>
  ) : (
    <NetworkConnectionButton
      aria-label="Network connection indicator"
      onClick={() => setNetworkConnectivityDrawerState('open')}
    >
      <StyledTooltip
        label={TOOLTIP_MESSAGE}
        direction="bottom"
      >
        <IconWrapper>
          <ConnectionIcon />
        </IconWrapper>
      </StyledTooltip>
    </NetworkConnectionButton>
  );
};

interface ConnectionIndicatorProps {
  participantId?: string;
  backgroundColor?: string;
  delay?: number;
}

export const ConnectionIndicator = ({
  participantId,
  backgroundColor = 'rgba(0, 0, 0, 70%)',
  delay = TIME_OUT,
}: ConnectionIndicatorProps): JSX.Element | null => {
  const downlinkQuality = useHMSStore(
    selectConnectionQualityByPeerID(participantId),
  )?.downlinkQuality;
  const connectionQuality = getConnectionQuality(downlinkQuality);

  const showIndicator = useConnectionIndicatorDelay(delay);

  if (!showIndicator || connectionQuality.type === INVALID_CONNECTION_QUALITY) {
    return null;
  }

  const { icon: ConnectionIcon } = connectionQuality;

  return (
    <IconWrapper backgroundColor={backgroundColor}>
      <ConnectionIcon />
    </IconWrapper>
  );
};
